import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Menu, { MenuItem } from '@app/components/popup/Menu';
import SVG from '@app/components/SVG';
import bookUser from '@app/images/bookUser.svg';
import chevronLeft from '@app/images/chevronLeft.svg';
import lock from '@app/images/lock.svg';
import doneRingRound from '@app/images/done-ring-round.svg';
import phoneOutgoing from '@app/images/phoneOutgoing.svg';
import scale from '@app/images/scale.svg';
import shieldCheck from '@app/images/shieldCheck.svg';
import triangleAlert from '@app/images/triangleAlert.svg';
import InfoImage from '@app/images/designSystem/indication/info.svg';
import {
  OnlineValidationType,
  PayeeMetricsResults,
  SearchStatusType,
  SergeeMetricResult,
  SergeeSearchResult,
  SergeeSearchScore,
} from '@mortee/domain/morteeAccountVerification';
import React from 'react';
import { z } from 'zod';
import MatchingKnoxDialog from './MatchingKnoxDialog';
import { getValidationText, ResultPageState } from './resultPageUtils';
import RowStatusIcon from './RowStatusIcon';
import {
  accountVerificationSchema,
  formatDate,
  formatMetricsLabel,
  formatSergeeMetricResult,
  formatVerificationResult,
  getAcceptButtonText,
  getIsNotValidatedMetric,
  getMetricsOrder,
  getScoreColor,
  INITIATE_KNOX_VERIFY,
  isAcceptButtonDisabled,
  LOADING,
  shouldShowAcceptButton,
  shouldShowKnoxValidationButton,
  sortVerificationDetails,
} from './searchPayeeUtils';

type AccountVerificationForm = z.infer<typeof accountVerificationSchema>;

export default function AccountVerificationQuickCheckResultPage({
  formData,
  verificationResult,
  isVerifying,
  resultPageState,
  onBack,
  onApprove,
  onCancelKnoxValidation,
  onContinueKnoxValidation,
  downloadBankAccountCertificate,
  preparingReportDownload,
}: {
  formData: AccountVerificationForm;
  verificationResult: SergeeSearchResult | null;
  isVerifying: boolean;
  resultPageState: ResultPageState;
  onBack: (toastmessage: string, wasApproved?: boolean) => void;
  onApprove: () => Promise<void>;
  onCancelKnoxValidation: () => Promise<void>;
  onContinueKnoxValidation: () => void;
  downloadBankAccountCertificate: () => void;
  preparingReportDownload: boolean;
}): JSX.Element {
  const { isApproving, isApproved, showMatchingDialog, matchingResults } = resultPageState;

  const handleDownloadCertificate = (): void => {
    if (preparingReportDownload) {
      return;
    }
    downloadBankAccountCertificate();
  };

  const menuItems: MenuItem[] = [
    {
      id: 'download-certificate',
      dataTestId: 'menu-download-certificate',
      text: 'Download',
      disabled: preparingReportDownload || !verificationResult?.validatedPayeeUniformId,
      onClick: handleDownloadCertificate,
    },
  ];

  if (isVerifying) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <p>Verifying account details...</p>
      </div>
    );
  }

  const overallStatus = verificationResult?.score;
  const formattedDate = formatDate(verificationResult?.validatedTimestamp || verificationResult?.createTimestamp || Date.now());
  const metricsOrder = getMetricsOrder();
  const details = Object.entries(verificationResult?.metricsResult || {});
  const sortedDetails = sortVerificationDetails(details, metricsOrder);

  return (
    <TooltipProvider delayDuration={100000}>
      <div data-testid='search-result-page' id='search-result-page' className='min-h-screen bg-background'>
        <div className='w-full max-w-6xl mx-auto p-6 pt-24'>
          <div className='flex flex-col md:flex-row gap-8'>
            {/* Left summary column */}
            <div className='w-full md:w-80 flex-shrink-0 text-card-foreground'>
              <Card data-testid='card-search-result-page' className={`p-6 flex flex-col gap-6 mb-12 relative`}>
                {/* Three-dot menu positioned at top right */}
                <div className='absolute right-4'>
                  <Menu id='card-actions-menu' dataTestId='card-actions-menu' menuItems={menuItems} popupPlacement='bottom-end' />
                </div>

                <div>
                  <div
                    id='search-result-method'
                    data-testid={verificationResult?.status}
                    className={`text-4xl font-bold ${getScoreColor(overallStatus || SergeeSearchScore.Unknown)}`}
                  >
                    {formatVerificationResult(verificationResult?.status || SearchStatusType.QuickCheckUnknown)}
                  </div>
                  <div className='flex items-center gap-2 mt-2 text-sm text-muted-foreground'>
                    {getValidationText(
                      verificationResult?.status || SearchStatusType.QuickCheckUnknown,
                      formattedDate,
                      verificationResult?.validatingUser,
                    )}
                  </div>
                </div>
              </Card>
              <div className='flex flex-col gap-0 mb-6' hidden={overallStatus === SergeeSearchScore.NotValidated}>
                <div className='flex items-center justify-between py-4 px-2' hidden={!verificationResult?.knoxId}>
                  <span className='text-sm flex items-start gap-2'>
                    <SVG accessibilityLabel={'Knox ID'} image={shieldCheck} height={18} />
                    Knox ID
                  </span>
                  <span className='font-medium'>{verificationResult?.knoxId || ''}</span>
                </div>
                <Separator hidden={!verificationResult?.knoxId} />
                <div className='flex items-center justify-between py-4 px-2 text-gray-400'>
                  <span className='text-sm flex items-start gap-2'>
                    <SVG
                      accessibilityLabel={'Validation level'}
                      image={shieldCheck}
                      height={18}
                      className='fill-gray-400 text-gray-400'
                    />
                    Validation level
                  </span>
                  <span className='font-medium' id='search-result-method' data-testid={verificationResult?.status}>
                    <SVG
                      accessibilityLabel={SergeeMetricResult.KnoxValidationRequired}
                      image={lock}
                      height={18}
                      className='fill-gray-400 text-gray-400'
                    />
                  </span>
                </div>
                <Separator />
                {verificationResult?.onlineValidationType === OnlineValidationType.LiinkVop && (
                  <div className='flex items-center justify-between py-4 px-2'>
                    <span className='text-sm flex items-start gap-2'>
                      <SVG
                        accessibilityLabel={'Validation level'}
                        image={doneRingRound}
                        height={18}
                        className='fill-gray-400 text-gray-400'
                      />
                      Checked via
                    </span>
                    <span
                      className='text-sm flex'
                      id='search-result-method'
                      data-testid={verificationResult?.onlineValidationType ?? OnlineValidationType.LiinkVop}
                    >
                      VoP
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} style={{ display: 'inline-block', cursor: 'pointer' }}>
                            <SVG accessibilityLabel={SergeeMetricResult.KnoxValidationRequired} image={InfoImage} height={18} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className='max-w-md'>
                          <p className='!mb-0'>
                            Validation is performed via the <strong>European Verification of Payee (VoP)</strong> scheme, where
                            results are determined by the recipient bank's internal records. Because these banks often employ
                            "fuzzy matching" logic, a match result indicates a high level of similarity but may not always
                            represent a 100% character-for-character alignment. Users requiring higher precision or more
                            deterministic results may find <strong>Knox Verify</strong> better suited for their requirements.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </div>
                )}
                <Separator hidden={verificationResult?.onlineValidationType !== OnlineValidationType.LiinkVop} />
                <div className='flex items-center justify-between py-4 px-2 text-gray-400'>
                  <span className='text-sm flex items-start gap-2'>
                    <SVG accessibilityLabel={'Crowd source'} image={bookUser} height={18} />
                    Crowd source
                  </span>
                  <span className='font-medium'>
                    <SVG
                      accessibilityLabel={SergeeMetricResult.KnoxValidationRequired}
                      image={lock}
                      height={18}
                      className='fill-gray-400 text-gray-400'
                    />
                  </span>
                </div>
                <Separator />
                <div className='flex items-center justify-between py-4 px-2'>
                  <span className='text-sm flex items-start gap-2 text-gray-400'>
                    <SVG accessibilityLabel={'Sanction list'} image={scale} height={18} />
                    Sanction list
                  </span>
                  <span className='font-medium'>
                    <SVG
                      accessibilityLabel={SergeeMetricResult.KnoxValidationRequired}
                      image={lock}
                      height={18}
                      className='fill-gray-400 text-gray-400'
                    />
                  </span>
                </div>
                <Separator />
                <div className='flex items-center justify-between py-4 px-2' hidden={true}>
                  <span className='text-sm flex items-start gap-2'>
                    <SVG accessibilityLabel={'Call back'} image={phoneOutgoing} height={18} />
                    Call back
                  </span>
                  <span className='font-medium'>Passed</span>
                </div>
              </div>
            </div>

            <div className='flex-1 flex flex-col text-foreground'>
              <div className='bg-card rounded-xl border p-0 overflow-hidden'>
                <div className='w-full'>
                  {sortedDetails
                    .filter(([, value]) => value.result !== SergeeMetricResult.DoNotShow)
                    .map(([key, value], idx) => (
                      <div
                        key={key}
                        id={key}
                        data-testid={value.value}
                        className={`grid grid-cols-12 items-start ${
                          idx < sortedDetails.length - 1 ? 'border-b border-border' : ''
                        } ${value.result === SergeeMetricResult.KnoxValidationRequired ? 'bg-gray-100' : ''} text-sm`}
                      >
                        <div
                          className={`col-span-3 py-4 self-center px-4 font-medium ${
                            getIsNotValidatedMetric(value.result) ? 'text-muted-foreground' : 'text-foreground'
                          }`}
                        >
                          <span className='flex items-center gap-2'>{formatMetricsLabel(key as keyof PayeeMetricsResults)}</span>
                        </div>
                        <div
                          className={`col-span-6 self-center py-4 px-4 ${
                            getIsNotValidatedMetric(value.result) ? 'text-muted-foreground' : 'text-foreground'
                          }`}
                        >
                          <div className='flex flex-col gap-1'>
                            {Array.isArray(value.value) && value.value.length > 1 ? (
                              value.value.map((item, index) => (
                                <div key={index} className='flex items-center gap-2'>
                                  {item || '--'}
                                </div>
                              ))
                            ) : (
                              <div className='flex items-center gap-2'>
                                {key === 'name' &&
                                  value.result === SergeeMetricResult.PartialMatch &&
                                  value.value[0] !== formData.payeeName && (
                                    <div className='relative group'>
                                      <SVG accessibilityLabel='' image={triangleAlert} height={16} className='text-yellow-500' />
                                      <div
                                        data-testid='changed-from-payee-name'
                                        className='absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg'
                                      >
                                        Changed from {formData.payeeName}
                                      </div>
                                    </div>
                                  )}
                                {value.result != SergeeMetricResult.KnoxValidationRequired && value.value[0]
                                  ? value.value[0]
                                  : '--'}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className='col-span-3 py-4 px-4 flex items-center gap-2'>
                          <RowStatusIcon status={value.result} />
                          <span
                            data-testid={`${key}-status`}
                            className={getIsNotValidatedMetric(value.result) ? 'text-muted-foreground' : 'text-foreground'}
                          >
                            {formatSergeeMetricResult(value.result)}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className='flex justify-between items-center gap-4 mt-8'>
            <Button
              data-testid='back-button-result-page'
              variant='ghost'
              className='w-fit flex items-center gap-2'
              style={{ color: 'var(--foreground)' }}
              onClick={(): void => onBack('Quick check results saved.', isApproved)}
            >
              <SVG accessibilityLabel={'Back'} image={chevronLeft} height={18} className='fill-foreground text-foreground' /> Back
            </Button>
            <div className='flex gap-4' hidden={verificationResult?.status === SearchStatusType.KnoxValidated}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} style={{ display: 'inline-block' }}>
                    <Button
                      data-testid='knox-validation-button'
                      disabled={true}
                      variant='outline'
                      style={{ color: 'var(--foreground)' }}
                      hidden={!shouldShowKnoxValidationButton(verificationResult?.status)}
                    >
                      {INITIATE_KNOX_VERIFY}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='!mb-0'>For email integration, please contact the Customer Success team</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip
                open={
                  getAcceptButtonText(isApproved, isApproving, verificationResult?.status) === INITIATE_KNOX_VERIFY
                    ? undefined
                    : false
                }
              >
                <TooltipTrigger asChild>
                  <span tabIndex={0} style={{ display: 'inline-block' }}>
                    <Button
                      data-testid='accept-button'
                      disabled={
                        isAcceptButtonDisabled(isApproving, isApproved, verificationResult?.status) ||
                        getAcceptButtonText(isApproved, isApproving, verificationResult?.status) === INITIATE_KNOX_VERIFY
                      }
                      onClick={shouldShowAcceptButton(verificationResult?.status) && !isApproved ? onApprove : undefined}
                      variant='default'
                      style={{ minWidth: 100 }}
                    >
                      {getAcceptButtonText(isApproved, isApproving, verificationResult?.status) === LOADING ? (
                        <span className='animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block'></span>
                      ) : (
                        getAcceptButtonText(isApproved, isApproving, verificationResult?.status)
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='!mb-0'>For email integration, please contact the Customer Success team</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
        <MatchingKnoxDialog
          open={showMatchingDialog}
          supplierName={matchingResults?.[0]?.supplierName}
          onContinue={onContinueKnoxValidation}
          onTerminate={onCancelKnoxValidation}
        />
      </div>
    </TooltipProvider>
  );
}
